// Eigenbau: Durchsuchbare Log-Ansicht für Pipeline-Job-Traces; Tremor bietet
// kein Terminal-/Log-Widget und eine Log-Bibliothek wäre für die
// ANSI-Zerlegung aus @cubic/shared zu schwer.
import { useEffect, useMemo, useRef, useState } from "react";
import {
  fasseLogZusammen,
  zerlegeLog,
  type AnsiFarbe,
} from "@cubic/shared";
import { Input } from "@/components/Input";
import { cx } from "@/lib/utils";

const ansiTextFarbe: Record<AnsiFarbe, string> = {
  schwarz: "text-gray-500",
  rot: "text-red-500",
  gruen: "text-emerald-500",
  gelb: "text-yellow-500",
  blau: "text-blue-400",
  magenta: "text-fuchsia-400",
  cyan: "text-cyan-400",
  weiss: "text-gray-200",
  hellrot: "text-red-300",
  hellgruen: "text-emerald-300",
  hellgelb: "text-yellow-200",
  hellblau: "text-blue-300",
  hellmagenta: "text-fuchsia-300",
  hellcyan: "text-cyan-300",
  hellweiss: "text-white",
  hellgrau: "text-gray-300",
};

const zeilenHintergrund = {
  fehler: "bg-red-500/15",
  erfolg: "bg-emerald-500/10",
  warnung: "bg-yellow-500/10",
  normal: "",
};

export function LogAnsicht({
  text,
  abgeschnitten,
}: {
  text: string;
  abgeschnitten: boolean;
}) {
  const [suche, setzeSuche] = useState("");
  const zeilen = useMemo(() => zerlegeLog(text), [text]);
  const zusammenfassung = useMemo(
    () => fasseLogZusammen(zeilen),
    [zeilen],
  );

  const gefiltert = useMemo(() => {
    const begriff = suche.trim().toLowerCase();
    if (!begriff) return zeilen;
    return zeilen.filter((zeile) =>
      zeile.segmente.map((s) => s.text).join("").toLowerCase().includes(begriff),
    );
  }, [zeilen, suche]);

  /*
   * Das Log öffnet am Ende, nicht am Anfang.
   *
   * Ein Job-Log beginnt mit dem Hochfahren des Runners und endet mit dem
   * Grund, warum der Lauf so ausgegangen ist. Wer es aufschlägt, sucht das
   * Ende — vorher scrollte man durch das Installationsprotokoll dorthin.
   *
   * Bei aktiver Suche bleibt der Sprung aus: Dort will man den Treffer sehen,
   * und der steht selten unten. Wird die Suche geleert, steht wieder das Ende
   * da, weil dann wieder das ganze Log gemeint ist.
   */
  const tafel = useRef<HTMLPreElement>(null);
  useEffect(() => {
    if (suche.trim()) return;
    const element = tafel.current;
    if (!element) return;
    element.scrollTop = element.scrollHeight;
  }, [text, suche]);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-500" data-testid="log-zusammenfassung">
          {zusammenfassung.zeilen} Zeilen ·{" "}
          <span className="text-red-500">{zusammenfassung.fehler} Fehler</span>
          {" · "}
          <span className="text-emerald-600 dark:text-emerald-400">
            {zusammenfassung.erfolg} Erfolge
          </span>
          {zusammenfassung.warnungen > 0 ? (
            <>
              {" · "}
              <span className="text-yellow-600 dark:text-yellow-400">
                {zusammenfassung.warnungen} Warnungen
              </span>
            </>
          ) : null}
          {abgeschnitten ? " · Log gekürzt" : null}
        </p>
        <Input
          type="search"
          placeholder="Im Log suchen …"
          value={suche}
          className="h-8 w-56 text-xs"
          aria-label="Im Log suchen"
          onChange={(e) => setzeSuche(e.target.value)}
        />
      </div>
      {/*
        Das Log ist eine Tafel: dunkel in beiden Themen, mit **eigener** heller
        Tinte. Vorher stand hier nur `bg-gray-950` und keine Schriftfarbe — die
        ungefärbten Segmente erbten damit die Tinte der Seite, also dunkles
        Grau auf Schwarz. Lesbar war nur, was eine ANSI-Farbe mitbrachte.

        `--tafel-tinte*` ist genau dafür da; `--tinte*` wäre hier Schrift auf
        Schrift, weil sie für helles Papier gedacht ist.
      */}
      <pre
        ref={tafel}
        className="max-h-96 overflow-auto bg-tafel p-3 text-xs leading-5 text-tafel-tinte2"
        role="region"
        aria-label="Job-Log"
        /*
          Ein rollbarer Bereich braucht den Fokus, sonst kommt man mit der
          Tastatur nicht zurück nach oben — und seit das Log am Ende öffnet,
          ist „nach oben" der übliche Weg.
        */
        tabIndex={0}
      >
        {gefiltert.length === 0 ? (
          <span className="text-tafel-tinte3">
            {suche.trim()
              ? "Keine Zeilen gefunden."
              : "Das Log ist leer."}
          </span>
        ) : (
          gefiltert.map((zeile) => (
            <div
              key={zeile.nummer}
              className={cx(
                "flex gap-3 px-1",
                zeilenHintergrund[zeile.art],
              )}
            >
              <span className="w-10 shrink-0 select-none text-right text-gray-600">
                {zeile.nummer}
              </span>
              <span className="whitespace-pre-wrap break-all">
                {zeile.segmente.length === 0 ? (
                  " "
                ) : (
                  zeile.segmente.map((segment, index) => (
                    <span
                      key={index}
                      className={segment.farbe ? ansiTextFarbe[segment.farbe] : undefined}
                    >
                      {segment.text}
                    </span>
                  ))
                )}
              </span>
            </div>
          ))
        )}
      </pre>
    </div>
  );
}
